import React, { useState } from 'react';
import { PiggyBank, TrendingUp, Calendar, DollarSign, Target, AlertTriangle, CheckCircle, Clock, Calculator, BarChart3, FileText, Globe } from 'lucide-react';
import Disclaimer from '../components/Disclaimer';

const RothIRAPlanner = () => {
  const [plannerData, setPlannerData] = useState({
    // Current Roth IRA Status
    totalBalance: '',
    contributions: '',
    conversions: '',
    earnings: '',
    firstContributionDate: '',
    
    // Withdrawal Planning
    plannedWithdrawalAmount: '',
    plannedWithdrawalDate: '',
    currentAge: 41,
    targetAge: 59.5,
    
    // Tax Bracket Planning
    currentTaxBracket: '',
    expectedRetirementBracket: '',
    otherIncome: '',
    
    // Costa Rica Planning
    costaRicaResidencyDate: '',
    foreignIncomeExclusion: '',
    
    // Conversion Strategy
    traditionalIRABalance: '',
    conversionAmount: '',
    conversionYear: ''
  });

  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPlannerData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateRothStrategy = () => {
    const totalBalance = parseFloat(plannerData.totalBalance) || 0;
    const contributions = parseFloat(plannerData.contributions) || 0;
    const conversions = parseFloat(plannerData.conversions) || 0;
    const earnings = parseFloat(plannerData.earnings) || 0;
    const plannedAmount = parseFloat(plannerData.plannedWithdrawalAmount) || 0;
    const currentAge = parseFloat(plannerData.currentAge) || 41;
    const targetAge = parseFloat(plannerData.targetAge) || 59.5;
    
    // Calculate years until qualified withdrawal
    const yearsUntilQualified = Math.max(0, targetAge - currentAge);
    
    // Calculate qualified vs non-qualified amounts
    const qualifiedAmount = yearsUntilQualified <= 0 ? plannedAmount : 0;
    const nonQualifiedAmount = yearsUntilQualified > 0 ? plannedAmount : 0;
    
    // Calculate penalties for non-qualified withdrawals
    const earlyWithdrawalPenalty = nonQualifiedAmount * 0.10;
    const incomeTaxOnEarnings = nonQualifiedAmount * 0.22; // Assuming 22% bracket
    
    // Calculate tax-free vs taxable portions
    const taxFreePortion = Math.min(plannedAmount, contributions + conversions);
    const taxablePortion = Math.max(0, plannedAmount - taxFreePortion);
    
    // Calculate total cost of early withdrawal
    const totalEarlyWithdrawalCost = earlyWithdrawalPenalty + incomeTaxOnEarnings;
    
    // Calculate savings from waiting
    const savingsFromWaiting = totalEarlyWithdrawalCost;
    
    // Generate recommendations
    const recommendations = generateRothRecommendations(
      totalBalance,
      contributions,
      plannedAmount,
      currentAge,
      yearsUntilQualified,
      totalEarlyWithdrawalCost
    );

    setResults({
      totalBalance,
      contributions,
      conversions,
      earnings,
      yearsUntilQualified,
      qualifiedAmount,
      nonQualifiedAmount,
      earlyWithdrawalPenalty,
      incomeTaxOnEarnings,
      taxFreePortion,
      taxablePortion,
      totalEarlyWithdrawalCost,
      savingsFromWaiting,
      recommendations
    });
  };

  const generateRothRecommendations = (balance, contributions, withdrawal, age, yearsUntilQualified, earlyCost) => {
    const recommendations = [];
    
    if (yearsUntilQualified > 0) {
      recommendations.push(`Wait ${yearsUntilQualified.toFixed(1)} years to avoid $${earlyCost.toFixed(0)} in penalties and taxes`);
      recommendations.push('Withdraw contributions first (always tax-free)');
      recommendations.push('Consider using other income sources while waiting');
    }
    
    if (contributions > 0) {
      recommendations.push(`You have $${contributions.toFixed(0)} in contributions that can be withdrawn tax-free immediately`);
    }
    
    if (balance > 1000000) {
      recommendations.push('Large account - consider Roth conversions during low-income years');
      recommendations.push('Plan withdrawals to stay in lower tax brackets');
    }
    
    if (age < 50) {
      recommendations.push('Consider Roth conversions now while in lower tax brackets');
      recommendations.push('Start 5-year clock early for future flexibility');
    }
    
    recommendations.push('Coordinate Roth withdrawals with rental property income timing');
    recommendations.push('Plan Costa Rica residency to optimize foreign income exclusion');
    
    return recommendations;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatPercentage = (rate) => {
    return `${rate.toFixed(1)}%`;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Roth IRA Strategy Planner</h1>
        <p className="text-gray-600">Optimize your large Roth IRA for tax-free withdrawals and Costa Rica transition</p>
        <Disclaimer variant="tax" />
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', name: 'Account Overview', icon: PiggyBank },
            { id: 'withdrawal', name: 'Withdrawal Planning', icon: DollarSign },
            { id: 'conversion', name: 'Conversion Strategy', icon: TrendingUp },
            { id: 'timing', name: 'Timing Optimization', icon: Calendar },
            { id: 'costaRica', name: 'Costa Rica Planning', icon: Globe }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="inline h-4 w-4 mr-2" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Input Form */}
        <div className="space-y-6">
          {/* Account Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">Roth IRA Account Status</h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Total Roth IRA Balance</label>
                <input
                  type="number"
                  name="totalBalance"
                  value={plannerData.totalBalance}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Total Contributions Made</label>
                <input
                  type="number"
                  name="contributions"
                  value={plannerData.contributions}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Total Conversions</label>
                <input
                  type="number"
                  name="conversions"
                  value={plannerData.conversions}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Total Earnings</label>
                <input
                  type="number"
                  name="earnings"
                  value={plannerData.earnings}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Date of First Contribution</label>
                <input
                  type="date"
                  name="firstContributionDate"
                  value={plannerData.firstContributionDate}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-blue-900 mb-2">Roth IRA Basics</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Contributions: Always tax-free when withdrawn</li>
                  <li>• Conversions: Tax-free after 5-year holding period</li>
                  <li>• Earnings: Tax-free after age 59.5 + 5-year rule</li>
                  <li>• No required minimum distributions (RMDs)</li>
                </ul>
              </div>
            </div>
          )}

          {/* Withdrawal Planning Tab */}
          {activeTab === 'withdrawal' && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">Withdrawal Strategy</h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Planned Withdrawal Amount</label>
                <input
                  type="number"
                  name="plannedWithdrawalAmount"
                  value={plannerData.plannedWithdrawalAmount}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Planned Withdrawal Date</label>
                <input
                  type="date"
                  name="plannedWithdrawalDate"
                  value={plannerData.plannedWithdrawalDate}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Your Current Age</label>
                <input
                  type="number"
                  name="currentAge"
                  value={plannerData.currentAge}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="41"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Target Age for Qualified Withdrawals</label>
                <input
                  type="number"
                  name="targetAge"
                  value={plannerData.targetAge}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="59.5"
                />
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-green-900 mb-2">Withdrawal Strategy Tips</h3>
                <ul className="text-sm text-green-800 space-y-1">
                  <li>• Withdraw contributions first (always tax-free)</li>
                  <li>• Wait until age 59.5 to avoid 10% penalty</li>
                  <li>• Meet 5-year holding period for conversions</li>
                  <li>• Coordinate with other income sources</li>
                </ul>
              </div>
            </div>
          )}

          {/* Conversion Strategy Tab */}
          {activeTab === 'conversion' && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">Traditional to Roth Conversion</h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Traditional IRA Balance</label>
                <input
                  type="number"
                  name="traditionalIRABalance"
                  value={plannerData.traditionalIRABalance}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Conversion Amount</label>
                <input
                  type="number"
                  name="conversionAmount"
                  value={plannerData.conversionAmount}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Conversion Year</label>
                <input
                  type="number"
                  name="conversionYear"
                  value={plannerData.conversionYear}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="2024"
                />
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-purple-900 mb-2">Conversion Strategy</h3>
                <ul className="text-sm text-purple-800 space-y-1">
                  <li>• Convert during low-income years</li>
                  <li>• Stay under key tax bracket thresholds</li>
                  <li>• Start 5-year clock early</li>
                  <li>• Consider partial conversions</li>
                </ul>
              </div>
            </div>
          )}

          {/* Timing Optimization Tab */}
          {activeTab === 'timing' && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">Timing Optimization</h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Current Tax Bracket</label>
                <select
                  name="currentTaxBracket"
                  value={plannerData.currentTaxBracket}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Select bracket</option>
                  <option value="10">10%</option>
                  <option value="12">12%</option>
                  <option value="22">22%</option>
                  <option value="24">24%</option>
                  <option value="32">32%</option>
                  <option value="35">35%</option>
                  <option value="37">37%</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Expected Retirement Tax Bracket</label>
                <select
                  name="expectedRetirementBracket"
                  value={plannerData.expectedRetirementBracket}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Select bracket</option>
                  <option value="10">10%</option>
                  <option value="12">12%</option>
                  <option value="22">22%</option>
                  <option value="24">24%</option>
                  <option value="32">32%</option>
                  <option value="35">35%</option>
                  <option value="37">37%</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Other Income Sources</label>
                <input
                  type="number"
                  name="otherIncome"
                  value={plannerData.otherIncome}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="0"
                />
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-yellow-900 mb-2">Timing Optimization</h3>
                <ul className="text-sm text-yellow-800 space-y-1">
                  <li>• Convert when in lower tax brackets</li>
                  <li>• Coordinate with rental property income</li>
                  <li>• Consider Costa Rica residency timing</li>
                  <li>• Plan around major life events</li>
                </ul>
              </div>
            </div>
          )}

          {/* Costa Rica Planning Tab */}
          {activeTab === 'costaRica' && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">Costa Rica Residency Planning</h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Planned Residency Date</label>
                <input
                  type="date"
                  name="costaRicaResidencyDate"
                  value={plannerData.costaRicaResidencyDate}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Foreign Earned Income Exclusion</label>
                <input
                  type="number"
                  name="foreignIncomeExclusion"
                  value={plannerData.foreignIncomeExclusion}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="120000"
                />
              </div>
              
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-orange-900 mb-2">International Tax Benefits</h3>
                <ul className="text-sm text-orange-800 space-y-1">
                  <li>• Foreign Earned Income Exclusion: $120,000 (2023)</li>
                  <li>• 183+ days in Costa Rica for residency</li>
                  <li>• Roth withdrawals remain tax-free</li>
                  <li>• Consider timing of residency transition</li>
                </ul>
              </div>
            </div>
          )}

          {/* Calculate Button */}
          <button
            onClick={calculateRothStrategy}
            className="w-full bg-blue-600 text-white px-4 py-3 rounded-md font-medium hover:bg-blue-700"
          >
            Calculate Roth Strategy
          </button>
        </div>

        {/* Results Display */}
        <div className="space-y-6">
          {results ? (
            <>
              {/* Roth IRA Summary */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Roth IRA Summary</h2>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Balance:</span>
                    <span className="font-medium">{formatCurrency(results.totalBalance)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Contributions:</span>
                    <span className="font-medium text-green-600">{formatCurrency(results.contributions)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Conversions:</span>
                    <span className="font-medium text-blue-600">{formatCurrency(results.conversions)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Earnings:</span>
                    <span className="font-medium text-purple-600">{formatCurrency(results.earnings)}</span>
                  </div>
                </div>
              </div>

              {/* Withdrawal Analysis */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Withdrawal Analysis</h2>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Years Until Qualified:</span>
                    <span className="font-medium">{results.yearsUntilQualified.toFixed(1)} years</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax-Free Portion:</span>
                    <span className="font-medium text-green-600">{formatCurrency(results.taxFreePortion)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Taxable Portion:</span>
                    <span className="font-medium text-red-600">{formatCurrency(results.taxablePortion)}</span>
                  </div>
                  {results.earlyWithdrawalPenalty > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Early Withdrawal Penalty:</span>
                      <span className="font-medium text-red-600">{formatCurrency(results.earlyWithdrawalPenalty)}</span>
                    </div>
                  )}
                  {results.incomeTaxOnEarnings > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Income Tax on Earnings:</span>
                      <span className="font-medium text-red-600">{formatCurrency(results.incomeTaxOnEarnings)}</span>
                    </div>
                  )}
                  <div className="border-t pt-3">
                    <div className="flex justify-between text-lg font-semibold">
                      <span>Total Early Withdrawal Cost:</span>
                      <span className="text-red-600">{formatCurrency(results.totalEarlyWithdrawalCost)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Savings from Waiting */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Savings from Waiting</h2>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {formatCurrency(results.savingsFromWaiting)}
                  </div>
                  <p className="text-sm text-gray-600">
                    Amount you'll save by waiting until qualified withdrawal
                  </p>
                </div>
              </div>

              {/* Recommendations */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Strategy Recommendations</h2>
                <div className="space-y-2">
                  {results.recommendations.map((rec, index) => (
                    <div key={index} className="flex items-start">
                      <Target className="h-4 w-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{rec}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <PiggyBank className="w-24 h-24 text-gray-300 mx-auto mb-6" />
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Plan Your Roth IRA Strategy</h2>
              <p className="text-gray-600 mb-6">
                Fill out the form on the left and click "Calculate Roth Strategy" to see your personalized Roth IRA optimization plan.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
                <div className="p-4 bg-green-50 rounded-lg">
                  <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <h3 className="font-medium text-green-900">Tax-Free Withdrawals</h3>
                  <p className="text-sm text-green-700">Optimize timing for maximum benefits</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <TrendingUp className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <h3 className="font-medium text-blue-900">Conversion Strategy</h3>
                  <p className="text-sm text-blue-700">Traditional to Roth conversions</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <Globe className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <h3 className="font-medium text-purple-900">International Planning</h3>
                  <p className="text-sm text-purple-700">Costa Rica residency benefits</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RothIRAPlanner;
